import { useMemo, useState } from 'react';
import { useQuery } from '@tanstack/react-query';
import { ChevronLeft, ChevronRight } from 'lucide-react';
import { DateTime } from 'luxon';
import { AnimatePresence } from 'motion/react';

import { Field } from '@/components/Field';
import { useCurrentUser } from '@/features/auth/useAuth';
import { formatDurationClock } from '@/features/tasks/duration';
import { TaskDetail } from '@/features/tasks/TaskDetail';
import { formatDuration } from '@/features/tasks/tree';
import { useTask, useToggleDone } from '@/features/tasks/useTasks';
import { api } from '@/lib/api';
import { cn } from '@/lib/cn';

interface DailyReport {
  date: string;
  summary: {
    task_seconds: number;
    client_seconds: number;
    break_seconds: number;
    blocks_completed: number;
    pomodoros_completed: number;
  };
  blocks: Array<{
    id: number;
    status: string;
    phase: string;
    ended_early: boolean;
    items_count: number;
    items_completed: number;
  }>;
  tasks: Array<{
    task_id: number;
    title: string;
    logged_seconds: number;
    estimate_seconds: number;
    delta_seconds: number;
    completed: boolean;
  }>;
  timeline: Array<{
    entry_id: number;
    task_id: number;
    title: string;
    started_at: string;
    ended_at: string | null;
    duration_seconds: number;
    completed: boolean;
    source: string;
  }>;
  in_progress: Array<{
    task_id: number;
    title: string;
    logged_seconds: number;
    first_started_at: string;
    entry_count: number;
  }>;
  completed_without_time: Array<{
    task_id: number;
    title: string;
    completed_at: string | null;
  }>;
  estimate_accuracy: { over: number; under: number; on_target: number };
}

const fmtMins = (s: number) => formatDuration(Math.round(s / 60)) ?? '0m';

function shiftDate(isoDate: string, days: number): string {
  return (
    DateTime.fromISO(isoDate).plus({ days }).toISODate() ??
    DateTime.local().toISODate() ??
    ''
  );
}

function formatClockTime(iso: string, timezone: string): string {
  return DateTime.fromISO(iso, { zone: 'utc' }).setZone(timezone).toFormat('h:mm a');
}

/**
 * Day journal + breakdown: timed work in order, open work you can complete,
 * completions without time, and estimate accuracy.
 */
export function DailyBreakdownReport() {
  const { data: user } = useCurrentUser();
  const timezone = user?.timezone || Intl.DateTimeFormat().resolvedOptions().timeZone;

  const [date, setDate] = useState(
    () => DateTime.now().setZone(timezone).toISODate() ?? '',
  );
  const [openTaskId, setOpenTaskId] = useState<number | null>(null);
  const toggleDone = useToggleDone();

  const report = useQuery<DailyReport>({
    queryKey: ['reports', 'daily', date],
    queryFn: () => api<DailyReport>(`/api/reports/daily?date=${encodeURIComponent(date)}`),
    enabled: Boolean(date),
  });

  const fetched = useTask(openTaskId);
  const openNode = useMemo(
    () => (fetched.data ? { ...fetched.data, children: [] } : null),
    [fetched.data],
  );

  const data = report.data;
  const dayLabel = useMemo(() => {
    const dt = DateTime.fromISO(date, { zone: timezone });
    if (!dt.isValid) return date;
    return dt.toFormat('cccc, LLL d');
  }, [date, timezone]);

  return (
    <section className="flex flex-col gap-6">
      <div className="flex flex-wrap items-end justify-between gap-3">
        <div>
          <h2 className="text-body font-semibold">Daily</h2>
          <p className="text-small text-text-secondary">{dayLabel}</p>
        </div>
        <div className="flex flex-wrap items-end gap-2">
          <button
            type="button"
            aria-label="Previous day"
            onClick={() => setDate((d) => shiftDate(d, -1))}
            className="pressable flex size-touch items-center justify-center rounded-lg bg-surface-2 text-text-secondary hover:bg-surface-3 hover:text-text"
          >
            <ChevronLeft size={18} strokeWidth={1.5} aria-hidden />
          </button>
          <Field
            label="Date"
            type="date"
            value={date}
            onChange={(e) => setDate(e.target.value)}
            className="w-auto"
          />
          <button
            type="button"
            aria-label="Next day"
            onClick={() => setDate((d) => shiftDate(d, 1))}
            className="pressable flex size-touch items-center justify-center rounded-lg bg-surface-2 text-text-secondary hover:bg-surface-3 hover:text-text"
          >
            <ChevronRight size={18} strokeWidth={1.5} aria-hidden />
          </button>
        </div>
      </div>

      {!data && report.isPending && (
        <div className="h-24 animate-pulse rounded-lg bg-surface-2" />
      )}

      {report.isError && !data && (
        <p role="alert" className="text-body text-danger">
          Could not load daily report.{' '}
          <button type="button" className="underline" onClick={() => report.refetch()}>
            Retry
          </button>
        </p>
      )}

      {data && (
        <div className="flex flex-col gap-6">
          <div className="grid grid-cols-2 gap-3 sm:grid-cols-4">
            <MiniStat label="Task time" value={fmtMins(data.summary.task_seconds)} primary />
            <MiniStat label="Break time" value={fmtMins(data.summary.break_seconds)} />
            <MiniStat label="Blocks done" value={String(data.summary.blocks_completed)} />
            <MiniStat label="Pomodoros" value={String(data.summary.pomodoros_completed)} />
          </div>

          <JournalSection
            title="Worked today"
            empty="No timed work this day."
            count={data.timeline.length}
          >
            <ul className="divide-y divide-border rounded-lg border border-border">
              {data.timeline.map((row) => (
                <li
                  key={row.entry_id}
                  className="flex items-center gap-3 px-3 py-2.5 text-small"
                >
                  <span className="w-16 shrink-0 tabular text-text-secondary">
                    {formatClockTime(row.started_at, timezone)}
                  </span>
                  <span className="w-[4.5rem] shrink-0 tabular text-text-tertiary">
                    {formatDurationClock(row.duration_seconds)}
                  </span>
                  <button
                    type="button"
                    onClick={() => setOpenTaskId(row.task_id)}
                    className={cn(
                      'pressable min-w-0 flex-1 truncate text-left hover:underline',
                      row.completed && 'text-text-tertiary line-through',
                    )}
                  >
                    {row.title}
                  </button>
                </li>
              ))}
            </ul>
          </JournalSection>

          <JournalSection
            title="Still open"
            empty="Nothing timed that is still unfinished."
            count={data.in_progress.length}
          >
            <ul className="divide-y divide-border rounded-lg border border-border">
              {data.in_progress.map((row) => (
                <li
                  key={row.task_id}
                  className="flex items-center gap-3 px-3 py-2.5 text-small"
                >
                  <button
                    type="button"
                    onClick={() =>
                      toggleDone({
                        id: row.task_id,
                        status: 'in_progress',
                        title: row.title,
                      })
                    }
                    aria-label={`Complete "${row.title}"`}
                    aria-pressed={false}
                    className={cn(
                      'pressable flex size-check shrink-0 items-center justify-center rounded-sm border',
                      'border-border-strong hover:border-accent',
                    )}
                  />
                  <div className="min-w-0 flex-1">
                    <button
                      type="button"
                      onClick={() => setOpenTaskId(row.task_id)}
                      className="pressable block max-w-full truncate text-left font-medium hover:underline"
                    >
                      {row.title}
                    </button>
                    <p className="text-label text-text-tertiary">
                      <span className="tabular">
                        {formatDurationClock(row.logged_seconds)}
                      </span>
                      {' · started '}
                      {formatClockTime(row.first_started_at, timezone)}
                      {row.entry_count > 1 ? ` · ${row.entry_count} sessions` : null}
                    </p>
                  </div>
                </li>
              ))}
            </ul>
          </JournalSection>

          <JournalSection
            title="Completed (no time)"
            empty="No completions without logged time."
            count={data.completed_without_time.length}
          >
            <ul className="divide-y divide-border rounded-lg border border-border">
              {data.completed_without_time.map((row) => (
                <li
                  key={row.task_id}
                  className="flex items-center gap-3 px-3 py-2.5 text-small"
                >
                  <span className="w-16 shrink-0 tabular text-text-secondary">
                    {row.completed_at
                      ? formatClockTime(row.completed_at, timezone)
                      : '-'}
                  </span>
                  <button
                    type="button"
                    onClick={() => setOpenTaskId(row.task_id)}
                    className="pressable min-w-0 flex-1 truncate text-left text-text-tertiary line-through hover:underline"
                  >
                    {row.title}
                  </button>
                </li>
              ))}
            </ul>
          </JournalSection>

          <div>
            <h3 className="mb-3 text-label font-medium uppercase text-text-tertiary">
              Estimates
            </h3>
            {data.tasks.length === 0 ? (
              <p className="text-small text-text-tertiary">
                No timed tasks to compare against estimates.
              </p>
            ) : (
              <div className="overflow-x-auto rounded-lg border border-border">
                <table className="w-full text-small">
                  <thead>
                    <tr className="border-b border-border text-left text-label uppercase text-text-tertiary">
                      <th className="px-3 py-2 font-medium">Task</th>
                      <th className="px-3 py-2 text-right font-medium">Logged</th>
                      <th className="px-3 py-2 text-right font-medium">Estimate</th>
                      <th className="px-3 py-2 text-right font-medium">Delta</th>
                    </tr>
                  </thead>
                  <tbody>
                    {data.tasks.map((t) => (
                      <tr key={t.task_id} className="border-b border-border last:border-0">
                        <td className="px-3 py-2">
                          <button
                            type="button"
                            onClick={() => setOpenTaskId(t.task_id)}
                            className={cn(
                              'pressable max-w-full truncate text-left hover:underline',
                              t.completed && 'text-text-tertiary line-through',
                            )}
                          >
                            {t.title}
                          </button>
                        </td>
                        <td className="tabular px-3 py-2 text-right">
                          {fmtMins(t.logged_seconds)}
                        </td>
                        <td className="tabular px-3 py-2 text-right">
                          {fmtMins(t.estimate_seconds)}
                        </td>
                        <td
                          className={cn(
                            'tabular px-3 py-2 text-right',
                            t.delta_seconds > 60 && 'text-warning',
                            t.delta_seconds < -60 && 'text-success',
                          )}
                        >
                          {t.delta_seconds >= 0 ? '+' : ''}
                          {fmtMins(Math.abs(t.delta_seconds))}
                        </td>
                      </tr>
                    ))}
                  </tbody>
                </table>
              </div>
            )}
            <p className="mt-2 text-small text-text-secondary">
              Estimate accuracy: {data.estimate_accuracy.on_target} on target,{' '}
              {data.estimate_accuracy.over} over, {data.estimate_accuracy.under} under
            </p>
          </div>
        </div>
      )}

      <AnimatePresence>
        {openNode && (
          <TaskDetail
            task={openNode}
            onClose={() => setOpenTaskId(null)}
            onOpenTask={setOpenTaskId}
          />
        )}
      </AnimatePresence>
    </section>
  );
}

function JournalSection({
  title,
  empty,
  count,
  children,
}: {
  title: string;
  empty: string;
  count: number;
  children: React.ReactNode;
}) {
  return (
    <div>
      <h3 className="mb-3 text-label font-medium uppercase text-text-tertiary">{title}</h3>
      {count === 0 ? <p className="text-small text-text-tertiary">{empty}</p> : children}
    </div>
  );
}

function MiniStat({
  label,
  value,
  primary = false,
}: {
  label: string;
  value: string;
  primary?: boolean;
}) {
  return (
    <div
      className={cn(
        'rounded-lg border px-3 py-2',
        primary
          ? 'border-accent-border bg-accent-quiet'
          : 'border-border bg-surface-1',
      )}
    >
      <p className={cn('text-label', primary ? 'text-accent' : 'text-text-tertiary')}>
        {label}
      </p>
      <p className={cn('tabular font-semibold', primary ? 'text-heading' : 'text-body')}>
        {value}
      </p>
    </div>
  );
}
